import * as React from "react";
import { CaseRunClient, ResultRow, DnaMatchResultItemModel, ClientResources } from "@app/api2/api";
import moment from "moment";
import { BPColumn } from "@common/BPReactTable";
import { toSci } from "@common/Utils";
import { SentryMatchHitDetails } from "./SentryMatchHitDetails";
import { BPDataTable3 } from "@app/components/React/Common/DataTable/BPDataTable3";
import { BPCheckbox } from "@/components/bp/bpcheckbox";

interface SentryMatchResultsProps {
    labId: number;
    caseId: number
    caseRunId: number;
    forPrint?: boolean;
}

interface DnaMatchResultItemModelEx extends DnaMatchResultItemModel {
    matchDetails: ResultRow[];
}

interface SentryMatchResultsState {
    matches: DnaMatchResultItemModelEx[];
    loading: boolean,
}

export class SentryMatchResults extends React.Component<SentryMatchResultsProps, SentryMatchResultsState> {
    client: CaseRunClient = new CaseRunClient();
    translationsContext: React.Context<ClientResources>;

    constructor(props, context) {
        super(props, context);
        this.state = { matches: [], loading: false };
        

    }

    async refresh(p: { page?: number, sortBy?: string, sortDirection?: string, perPage?: number, filters?: Array<{ id: string, value: any }> }) {
        let hideInterSampleDeduction = p.filters.find(x => x.id == 'hideInterSampleDeduction' && x.value)?.value ?? false
        let countHomozygOnce = p.filters.find(x => x.id == 'countHomozygOnce' && x.value)?.value ?? false
        let intraCaseOnly = p.filters.find(x => x.id == 'intraCaseOnly' && x.value)?.value ?? false
        let filterBy2P = p.filters.find(x => x.id == 'filterBy2P' && x.value)?.value ?? false
        let pageData = await this.client.getSentryResults(this.props.labId, this.props.caseId, this.props.caseRunId, hideInterSampleDeduction, countHomozygOnce, intraCaseOnly, filterBy2P, p.perPage, p.page, p.sortBy, p.sortDirection);
        this.setState({ ...this.state, matches: pageData.items.map(m => { return { ...m, matchDetails: [] } }) })
        return pageData;
    }

    async loadDetails(row: DnaMatchResultItemModelEx) {
        let results = await this.client.getSentryResultDetail(this.props.labId, this.props.caseId, this.props.caseRunId, row.resultId);

        row.matchDetails = results;

        this.setState({ ...this.state });
    }

    render() {
        const dotdotdotStyle: React.CSSProperties = { 'overflow': 'hidden', 'textOverflow': 'ellipsis', whiteSpace: "nowrap" };

        const columns2: Array<BPColumn<DnaMatchResultItemModel>> = [
            {
                Filter: (filter) => <BPCheckbox label={window.translation.caseRun.use2pforHomozygousDeductions} checked={filter.column.filterValue} onChange={(e, { checked }) => filter.setFilter(filter.column.id, checked)} />,
                Header: '',
                id: 'filterBy2P',
                disableSortBy: true,
                width: "0px",
                hidden: true
            },
            {
                Filter: (filter) => <BPCheckbox label={window.translation.caseRun.intraCaseHitsOnly} checked={filter.column.filterValue} onChange={(e, { checked }) => filter.setFilter(filter.column.id, checked)} />,
                Header: '',
                id: 'intraCaseOnly',
                disableSortBy: true,
                width: "0px",
                hidden: true
            },
            {
                Filter: (filter) => <BPCheckbox label={window.translation.caseRun.use2pMACforHomozygousDeductions} checked={filter.column.filterValue} onChange={(e, { checked }) => filter.setFilter(filter.column.id, checked)} />,
                Header: '',
                id: 'countHomozygOnce',
                disableSortBy: true,
                width: "0px",
                hidden: true
            },
            {
                Filter: (filter) => <BPCheckbox label={window.translation.caseRun.hideInterSampleDeductionMatches} checked={filter.column.filterValue} onChange={(e, { checked }) => filter.setFilter(filter.column.id, checked)} />,
                Header: '',
                id: 'hideInterSampleDeduction',
                disableSortBy: true,
                width: "0px",
                hidden: true
            },
            {
                Header: window.translation.common.case,
                accessor: 'otherCaseName',
                disableSortBy: true,
                width: "15%",
                // width: "140px",
                Cell: item => <a style={dotdotdotStyle} title={item.value} href={item.row.original.otherCaseUrl}>{item.value}</a>,
            },
            {
                Header: window.translation.common.created,
                accessor: 'otherCaseDate',
                disableSortBy: true,
                Cell: item => <span>{moment(item.value).format("MM/DD/YYYY")}</span>,
                // width: "110px",
                width: "10%",
            },
            {
                Header: window.translation.common.reference,
                accessor: 'referenceName',
                Cell: item => <a style={dotdotdotStyle} title={`${item.value} (${item.row.original.referenceCaseName})`} href={item.row.original.referenceUrl}>{item.value}</a>,
            },
            {
                Header: window.translation.common.evidence,
                accessor: 'evidenceName',
                Cell: item => <a style={dotdotdotStyle} title={`${item.value} (${item.row.original.evidenceCaseName})`} href={item.row.original.evidenceUrl}>{item.value}</a>,
            },
            {
                Header: window.translation.caseRun.commonLoci,
                accessor: 'commonLoci',
                // width: "60px",
                width: "5%",
            },
            {
                Header: window.translation.caseRun.totalAlleleCount,
                accessor: 'totalAlleleCount',
                // width: "60px",
                width: "5%",
            },
            {
                Header: window.translation.caseRun.mac,
                accessor: 'mac',
                Cell: cell => <>{cell.value.toPrecision(2)}</>,
                // width: "60px",
                width: "5%",
            },
            {
                Header: window.translation.caseRun.twoPMAC,
                accessor: 'macHomozygOnce',
                Cell: cell => <>{cell.value.toPrecision(2)}</>,
                // width: "60px",
                width: "5%",
            },
            {
                Header: window.translation.caseRun.noc,
                accessor: 'nContr',
                // width: "60px",
                width: "5%",
            },
            {
                Header: 'LR',
                accessor: 'lrQualLog10',
                // cellTitle:  true,
                Cell: cell => <span title={toSci(cell.value, 4) + '' + (toSci(cell.row.original.lrQualLog102P, 4) != toSci(cell.value, 4) ? ' / ' + toSci(cell.row.original.lrQualLog102P, 4) : '')}>{toSci(cell.value, 4) + '' + (toSci(cell.row.original.lrQualLog102P, 4) != toSci(cell.value, 4) ? ' / ' + toSci(cell.row.original.lrQualLog102P, 4) : '')}</span>,
                // width: "120px",
                width: "10%",
            },
            // {
            //     Header: 'LR 2P',
            //     accessor: 'lrQualLog102P',
            //     Cell: cell => toSci(cell.value, 4),
            //     // width: "120px",
            //     width: "10%",
            // },
            {
                Header: '',
                id: 'btns',
                Cell: item => <span>
                    <a title={window.translation.common.download} href={item.row.original.exportUrl}><i className="large icon excel"></i></a>
                </span>,
                width: "5%",
                hidden: this.props.forPrint
                // width: "52px",
            }
        ];

        return (
            <BPDataTable3
                forPrint={this.props.forPrint}
                storeKey={"SentryResults"}
                fixed
                singleLine
                columns={columns2}
                dataSource={p => this.refresh(p)}
                expandableRowsComponent={((row: DnaMatchResultItemModel) => <SentryMatchHitDetails resultId={row.resultId} {...this.props} />)}
            />
        );
    }
}


